import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Trophy, AlertTriangle, CheckCircle, X, ChevronRight, Download } from 'lucide-react';
import DateRangePicker from '../components/DateRangePicker';
import { exportCsv } from '../utils/exportCsv';

function ScoreBar({ rate }) {
  if (rate == null) return <span className="text-gray-300 text-xs">—</span>;
  const color = rate >= 90 ? 'bg-green-500' : rate >= 75 ? 'bg-amber-400' : 'bg-red-400';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-gray-100 rounded-full h-2">
        <div className={`${color} h-2 rounded-full`} style={{ width: `${Math.min(rate,100)}%` }} />
      </div>
      <span className={`text-xs font-semibold w-10 text-right ${rate >= 90 ? 'text-green-600' : rate >= 75 ? 'text-amber-600' : 'text-red-600'}`}>
        {rate}%
      </span>
    </div>
  );
}

function TechTable({ techs, onSelect }) {
  if (!techs || techs.length === 0)
    return <div className="px-4 py-4 text-center text-xs text-gray-400">Not enough data yet</div>;
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-gray-50 border-b border-gray-100">
          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 w-8">#</th>
          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Technician</th>
          <th className="px-4 py-2 text-center text-xs font-semibold text-gray-500">Done</th>
          <th className="px-4 py-2 text-center text-xs font-semibold text-gray-500">Pass</th>
          <th className="px-4 py-2 text-center text-xs font-semibold text-gray-500">Flag</th>
          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 w-36">Pass Rate</th>
        </tr>
      </thead>
      <tbody>
        {techs.map((t, i) => (
          <tr key={t.primary_tech} className={`border-b border-gray-50 ${i%2===0?'bg-white':'bg-gray-50/30'}`}>
            <td className="px-4 py-2.5 text-xs font-bold text-gray-400">{i + 1}</td>
            <td className="px-4 py-2.5">
              <button
                onClick={() => onSelect(t)}
                className="font-medium text-[#E8761A] hover:text-[#3D2B1A] hover:underline flex items-center gap-1 text-sm"
              >
                {t.primary_tech}
                <ChevronRight size={13} className="opacity-50" />
              </button>
            </td>
            <td className="px-4 py-2.5 text-center text-gray-600 text-xs">{t.total_completed}</td>
            <td className="px-4 py-2.5 text-center text-green-600 font-semibold text-xs">{t.passed ?? 0}</td>
            <td className="px-4 py-2.5 text-center text-red-500 font-semibold text-xs">{t.flagged ?? 0}</td>
            <td className="px-4 py-2.5"><ScoreBar rate={t.pass_rate} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TechPanel({ tech, branch, onClose }) {
  const [jobs, setJobs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!tech) return;
    setLoading(true);
    api.get('/jobs/completed', {
      params: { tech: tech.primary_tech, branch, per_page: 200 }
    })
      .then(r => setJobs(r.data.jobs || []))
      .finally(() => setLoading(false));
  }, [tech, branch]);

  if (!tech) return null;

  const passed  = jobs.filter(j => j.result === 'Pass');
  const flagged = jobs.filter(j => j.result === 'Flag');

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl z-50 flex flex-col">
        <div className="bg-[#3D2B1A] text-white px-6 py-4 flex items-start justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold">{tech.primary_tech}</h2>
            <p className="text-white/60 text-sm mt-0.5">{branch}</p>
            <div className="flex gap-4 mt-2 text-sm">
              <span>{jobs.length} completed</span>
              <span className="text-green-400">{passed.length} Pass</span>
              <span className="text-red-400">{flagged.length} Flag</span>
              {tech.pass_rate != null && (
                <span className="text-[#E8761A] font-semibold">{tech.pass_rate}% pass rate</span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white mt-1">
            <X size={20} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-gray-400">Loading jobs...</div>
          ) : jobs.length === 0 ? (
            <div className="p-8 text-center text-gray-400">No completed jobs found</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {jobs.map(job => (
                <div key={job.id} className="px-6 py-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <span
                          className="font-mono text-[#E8761A] text-sm font-semibold underline decoration-dotted cursor-pointer hover:text-[#3D2B1A]"
                          onClick={() => navigate(`/audit/${job.id}`)}
                        >
                          #{job.job_number}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          job.result === 'Pass' ? 'bg-green-100 text-green-700' :
                          job.result === 'Flag' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-500'}`}>
                          {job.result || '—'}
                        </span>
                        {job.score != null && (
                          <span className={`text-xs font-semibold ${job.score >= 0.9 ? 'text-green-600' : job.score >= 0.75 ? 'text-amber-600' : 'text-red-600'}`}>
                            {Math.round(job.score * 100)}%
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500">
                        {job.job_type && <span>{job.job_type}</span>}
                        {job.customer_name && <span>{job.customer_name}</span>}
                        {job.scheduled_date && <span>{new Date(job.scheduled_date).toLocaleDateString()}</span>}
                        {job.completed_at && (
                          <span className="text-gray-400">Completed {new Date(job.completed_at).toLocaleDateString()}</span>
                        )}
                      </div>
                      {job.notes && job.notes !== '0' && (
                        <div className="mt-2 px-3 py-2 bg-amber-50 border border-amber-100 rounded-lg">
                          <div className="text-xs font-semibold text-amber-700 mb-0.5">Auditor Notes</div>
                          <div className="text-xs text-amber-800 leading-relaxed">{job.notes}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default function TechHighlights() {
  const [data,     setData]     = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [selected, setSelected] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');
  const [branchFilter, setBranchFilter] = useState('');

  const loadData = useCallback(() => {
    setLoading(true);
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo)   params.date_to   = dateTo;
    api.get('/reports/tech-highlights', { params })
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) return <div className="p-8 text-gray-400">Loading...</div>;
  if (!data.length) return (
    <div className="p-8 text-gray-400">No completed audit data yet.</div>
  );

  function openTech(tech, branch) { setSelected({ tech, branch }); }
  function close() { setSelected(null); }

  function handleExport() {
    const rows = [];
    const source = branchFilter ? data.filter(b => b.branch === branchFilter) : data;
    source.forEach(b => {
      b.top_by_pass_rate.forEach(t => rows.push({ Category: 'Top Pass Rate', Branch: b.branch, Tech: t.primary_tech, Completed: t.total_completed, Passed: t.passed??0, Flagged: t.flagged??0, 'Pass Rate': t.pass_rate!=null?t.pass_rate+'%':'' }));
      b.top_by_passed.forEach(t => rows.push({ Category: 'Top Passed', Branch: b.branch, Tech: t.primary_tech, Completed: t.total_completed, Passed: t.passed??0, Flagged: t.flagged??0, 'Pass Rate': t.pass_rate!=null?t.pass_rate+'%':'' }));
      b.bottom_by_pass_rate.forEach(t => rows.push({ Category: 'Needs Attention', Branch: b.branch, Tech: t.primary_tech, Completed: t.total_completed, Passed: t.passed??0, Flagged: t.flagged??0, 'Pass Rate': t.pass_rate!=null?t.pass_rate+'%':'' }));
    });
    exportCsv(`tech-highlights${branchFilter ? `-${branchFilter}` : ''}.csv`, rows);
  }

  return (
    <div className="p-8 space-y-10">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Technician Highlights</h1>
          <p className="text-sm text-gray-500 mt-1">
            Top performers per branch — click a name to see their jobs and notes
          </p>
        </div>
        <div className="flex items-center gap-3">
          <DateRangePicker dateFrom={dateFrom} dateTo={dateTo} onChange={(f,t) => { setDateFrom(f); setDateTo(t); }} />
          <select value={branchFilter} onChange={e => setBranchFilter(e.target.value)}
            className="text-xs bg-white border border-gray-200 rounded-lg px-2 py-2 text-gray-600 outline-none">
            <option value="">All Branches</option>
            {data.map(b => <option key={b.branch} value={b.branch}>{b.branch}</option>)}
          </select>
          <button onClick={handleExport} className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50">
            <Download size={13} /> Export CSV
          </button>
        </div>
      </div>

      {/* ── TOP PERFORMERS (per branch) ── */}
      {data.map(branch => (
        <div key={branch.branch} className="space-y-4">
          <div className="bg-[#3D2B1A] text-white px-5 py-3 rounded-xl flex items-center justify-between">
            <span className="text-sm font-bold tracking-wider uppercase">{branch.branch}</span>
            <span className="text-white/50 text-xs">
              {branch.all_count} tech{branch.all_count !== 1 ? 's' : ''} with completed audits
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Top 3 by Pass Rate */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2 bg-green-50">
                <Trophy size={15} className="text-green-600" />
                <span className="text-sm font-semibold text-green-800">Top 3 — Pass Rate</span>
              </div>
              <TechTable techs={branch.top_by_pass_rate} onSelect={t => openTech(t, branch.branch)} />
            </div>

            {/* Top 3 by Most Passed */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2 bg-orange-50">
                <CheckCircle size={15} className="text-[#E8761A]" />
                <span className="text-sm font-semibold text-[#3D2B1A]">Top 3 — Most Passed</span>
              </div>
              <TechTable techs={branch.top_by_passed} onSelect={t => openTech(t, branch.branch)} />
            </div>
          </div>
        </div>
      ))}

      {/* ── NEEDS ATTENTION (all branches, bottom of page) ── */}
      <div className="pt-4 border-t-2 border-red-100 space-y-4">
        <div className="flex items-center gap-3">
          <AlertTriangle size={20} className="text-red-500" />
          <div>
            <h2 className="text-lg font-bold text-gray-900">Needs Attention</h2>
            <p className="text-xs text-gray-500">Bottom 3 by pass rate per branch — click names to review their jobs</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {data.map(branch => (
            <div key={branch.branch} className="bg-white rounded-xl shadow-sm border border-red-100 overflow-hidden">
              <div className="px-4 py-3 border-b border-red-100 flex items-center justify-between bg-red-50">
                <span className="text-sm font-semibold text-red-800">{branch.branch}</span>
                <AlertTriangle size={13} className="text-red-400" />
              </div>
              <TechTable techs={branch.bottom_by_pass_rate} onSelect={t => openTech(t, branch.branch)} />
            </div>
          ))}
        </div>
      </div>

      {selected && (
        <TechPanel tech={selected.tech} branch={selected.branch} onClose={close} />
      )}
    </div>
  );
}
